import {
    getPaymentMethod,
    listPaymentMethods
} from './queries';
import {
    createPaymentMethod,
    deletePaymentMethod,
    updatePaymentMethod,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {
    CreatePaymentMethod,
    DeletePaymentMethod,
    GetPaymentMethod,
    ListPaymentMethods,
    PaymentMethod,
    UpdatePaymentMethod
} from "../types.ts";
import {generateClient} from "aws-amplify/api";


const client = generateClient();

export const fetchRecords = async (filter: object, limit?: number, nextToken?: string) => {

    const response = await client.graphql<GraphQLQuery<ListPaymentMethods>>({
                                                                                query: listPaymentMethods,
                                                                                variables: {filter: filter, limit, nextToken},
                                                                            });
    return response.data?.listPaymentMethods;
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetPaymentMethod>>({
                                                                              query: getPaymentMethod,
                                                                              variables: {id: id},
                                                                          });
    return response.data?.getPaymentMethod;
};

/**
 * Copies only the declared fields off `source` into a new object, so the
 * caller's record is never mutated. An absent field stays absent rather than
 * being sent as `undefined`.
 */
const pickDeclared = (
    source: Record<string, unknown>,
    fields: readonly string[],
): Record<string, unknown> =>
    fields.reduce<Record<string, unknown>>((input, field) => {
        if (typeof source[field] !== 'undefined') {
            input[field] = source[field];
        }
        return input;
    }, {});

/**
 * Picks a nested input object. A missing or null value is omitted: AppSync
 * reads an explicit null as "clear this", and a non-card payment method has no
 * card at all.
 */
const pickNested = (
    value: unknown,
    fields: readonly string[],
): Record<string, unknown> | undefined =>
    value !== null && typeof value === 'object'
        ? pickDeclared(value as Record<string, unknown>, fields)
        : undefined;

/**
 * Fields `BillingDetailsInput` declares.
 */
const BILLING_DETAILS_INPUT_FIELDS = ['address', 'email', 'name', 'phone'] as const;

/**
 * Fields `PaymentMethodCardInput` declares. This is NOT the read shape: the
 * loaded record's card carries `brand`, `country`, `funding`, `last4` and
 * `fingerprint`, none of which the input declares.
 */
const CARD_INPUT_FIELDS = ['number', 'expMonth', 'expYear', 'cvc'] as const;

/**
 * Top-level fields `CreatePaymentMethodInput` declares, other than the two
 * nested inputs, which are picked separately.
 */
const CREATE_INPUT_SCALAR_FIELDS = ['type', 'customerId'] as const;

/**
 * ═══ AN ALLOWLIST, NOT A DENYLIST (FNP-1920) ═══
 *
 * `CreatePaymentMethodInput` declares exactly `{type, billingDetails, card,
 * customerId}`. The hook used to spread the form values and delete `created`,
 * which sent every other field too. Built by copying declared fields out, so
 * the caller's values are never touched (FNP-996).
 *
 * Verified against
 * BILLING/APIS/flexinet-stripe-api/src/appsync-api/src/schema/features/paymentMethods.graphql.
 */
export const add = async (record: PaymentMethod) => {

    const payload = pickDeclared(record as unknown as Record<string, unknown>, CREATE_INPUT_SCALAR_FIELDS);

    const billingDetails = pickNested(record.billingDetails, BILLING_DETAILS_INPUT_FIELDS);
    if (billingDetails) {
        payload.billingDetails = billingDetails;
    }

    const card = pickNested(record.card, CARD_INPUT_FIELDS);
    if (card) {
        payload.card = card;
    }

    const response = await client.graphql<GraphQLQuery<CreatePaymentMethod>>({
                                                                                 query: createPaymentMethod,
                                                                                 variables: {input: payload},
                                                                             });

    return response.data?.createPaymentMethod;
};

/**
 * ═══ AN ALLOWLIST, NOT A DENYLIST (FNP-1112) ═══
 *
 * `UpdatePaymentMethodInput` declares exactly `{id, billingDetails, card}`.
 * This was `{...record}` with `created` and `livemode` deleted, which sent
 * `customerId`, `type`, `createdAtMillis` and the flattened `card*` sort fields
 * too. This module is the one others were copied from, so the shape here is the
 * one that propagates.
 *
 * The payload is built by copying declared fields out, at every level, so the
 * caller's record (a live Formik value or a React Query cache entry) is never
 * touched (FNP-996).
 *
 * Verified against
 * BILLING/APIS/flexinet-stripe-api/src/appsync-api/src/schema/features/paymentMethods.graphql.
 */
export const update = async (id: string, record: PaymentMethod) => {

    // `id` comes from the argument, matching payment-intents and setup-intents.
    const payload: Record<string, unknown> = {id: id || record.id};

    const billingDetails = pickNested(record.billingDetails, BILLING_DETAILS_INPUT_FIELDS);
    if (billingDetails) {
        payload.billingDetails = billingDetails;
    }

    const card = pickNested(record.card, CARD_INPUT_FIELDS);
    if (card) {
        payload.card = card;
    }

    const response = await client.graphql<GraphQLQuery<UpdatePaymentMethod>>({
                                                                                 query: (updatePaymentMethod),
                                                                                 variables: {input: payload},
                                                                             });
    return response.data?.updatePaymentMethod;
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeletePaymentMethod>>({
                                                                                 query: (deletePaymentMethod),
                                                                                 variables: {input: {id: id}},

                                                                             });
    return response.data?.deletePaymentMethod;
};
